import { useEffect } from 'react';
import { useOrderActivityFeed } from '../../../../../../../services/modules/activityFeed/activityFeed.queries';
import { ORDER_ACTIVITY_FEED_DOMAINS } from '../../../../../../activity-feed/activityFeed.constants';
import { useActivityFeedFilterSearchParams } from '../../../../../../activity-feed/hooks/useActivityFeedFilterSearchParams';
import {
  isAccessDeniedError,
  shouldRetryActivityFeed,
} from '../../../../../../activity-feed/utils/activityFeedError.utils';
import {
  getDayGroupCounts,
  groupItemsByDay,
} from '../../../../../../activity-feed/utils/activityFeedGrouping.utils';
import { resolveActivityFeedRows } from '../../../../../../activity-feed/utils/activityFeedRows.utils';
import { useApplicationContext } from '../../../../../../context/ApplicationContext/ApplicationContext';

interface UseOrderObjectActivitiesTabParams {
  orderId: string;
}

const useOrderObjectActivitiesTab = ({ orderId }: UseOrderObjectActivitiesTabParams) => {
  const filters = useActivityFeedFilterSearchParams();
  const { hasOrderIntegration } = useApplicationContext();

  const domains = hasOrderIntegration
    ? ORDER_ACTIVITY_FEED_DOMAINS
    : ORDER_ACTIVITY_FEED_DOMAINS.filter((domain) => domain !== 'integration');

  const {
    pages,
    pagesData,
    error,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useOrderActivityFeed({ orderId, ...filters }, { retry: shouldRetryActivityFeed });

  const items = resolveActivityFeedRows(pagesData ?? [], { orderId });
  const groups = groupItemsByDay({ items, getDate: (item) => item.row.occurredAt });
  const hasNoVisibleRow = items.length === 0;

  const lastPage = pages?.at(-1);
  const hasNoVisibleRowInLastPage =
    !!lastPage && resolveActivityFeedRows(lastPage.data, { orderId }).length === 0;

  const isSkippingPagesWithoutVisibleRow = hasNoVisibleRowInLastPage && hasNextPage && !isError;

  useEffect(() => {
    if (isSkippingPagesWithoutVisibleRow && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [isSkippingPagesWithoutVisibleRow, isFetchingNextPage, fetchNextPage]);

  return {
    filters,
    domains,
    groups,
    groupCounts: getDayGroupCounts(groups),
    isEmpty: hasNoVisibleRow && !hasNextPage,
    isLoading: isLoading || (hasNoVisibleRow && isSkippingPagesWithoutVisibleRow),
    isError,
    isAccessDenied: isAccessDeniedError(error),
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  };
};

export default useOrderObjectActivitiesTab;
